import { Feature, Geometry, Polygon } from 'geojson';
import { DeclareMethod } from '/imports/methods';
import { ZoneLayer } from '/imports/api/incentiveZones';

// What the grid can take, from the utilities' own Integration Capacity Analysis maps. All three of
// California's big utilities publish theirs openly, each in its own field names, its own geometry and
// its own units — SDG&E draws a substation as its service polygon and a circuit as a grid of cells,
// SCE and PG&E draw a point and a line, and PG&E states its figures in kilowatts — so all three are
// mapped onto the shape below and the map treats them alike. Read live, never stored: the utilities
// restate these figures as their analyses are rerun.

export type GridUtility = 'sdge' | 'sce' | 'pge';

export interface SubstationProperties {
  lse: GridUtility;
  name?: string;
  /** as published, e.g. '69/12 kV' */
  voltage?: string;
  /** MW already generating on the substation */
  existingGeneration?: number;
  /** MW in the interconnection queue */
  queuedGeneration?: number;
  totalGeneration?: number;
  /** MW of load the utility projects */
  projectedLoad?: number;
  /** generation as a percentage of projected load */
  penetration?: number;
  /** MW still available, where the utility states one */
  remainingCapacity?: number;
  note?: string;
}
export type Substation = Feature<Geometry, SubstationProperties>;

export interface CircuitProperties {
  lse: GridUtility;
  circuit?: string;
  substation?: string;
  /** kV, as published */
  voltage?: string;
  /** MW of PV the segment can take */
  icaPv?: number;
  /** MW of PV it can take where the utility may curtail — usually the lower figure */
  icaPvOpFlex?: number;
  /** MW of fixed-output generation it can take where the utility may curtail */
  icaUniformOpFlex?: number;
  /** MW of load it can take */
  icaLoad?: number;
  /** 'OH' overhead or 'UG' underground, where published */
  ohug?: string;
  /** what caps the figure, e.g. thermal or steady-state voltage */
  limitingFactor?: string;
  section?: string;
  /** MW already generating on the whole circuit, where the utility totals it (SDG&E) */
  connectedGeneration?: number;
  /** MW queued on the whole circuit */
  queuedGeneration?: number;
  totalGeneration?: number;
  /** luminia1's estimate of what a feeder has left: its nominal rating less everything connected or queued */
  headroom?: number;
  /** the circuit's customers by class, as percentages of the total, where published */
  customers?: { residential?: number; commercial?: number; industrial?: number; agricultural?: number };
}
export type Circuit = Feature<Geometry, CircuitProperties>;

/** The nominal MW a 12 kV feeder carries in luminia1's headroom estimate; other voltages have no estimate. */
export const feederNominalMw: { [voltageKv: string]: number } = { '12': 12 };

/** luminia1's reading of a circuit's headroom, kept: 2 MW is roomy, 1 MW is tight, below that is full. */
export const circuitCapacityMw = { 'roomy': 2, 'tight': 1 };

export const listSubstationsInPolygon = DeclareMethod<{ polygon: Polygon }, ZoneLayer<Substation>>('listSubstationsInPolygon');
export const listCircuitsInPolygon = DeclareMethod<{ polygon: Polygon }, ZoneLayer<Circuit>>('listCircuitsInPolygon');
